// FT-184 REQ-16: универсальный рендер итога (`effect`) отложенной операции агента.
//
// Итог — снимок, который сервер считает для каждого инструмента записи; форма у
// каждого своя (одна операция — объект, пачка — массив). Инструментов записи на
// момент STEP-06b ещё нет, поэтому здесь нет ни одной ветки под конкретный
// инструмент: только общие правила.
//   - объект → пары «ключ — значение», вложенные объекты — вложенным блоком;
//   - массив записей → нумерованный список; массив простых значений (числа, строки, даты) — строкой через запятую;
//   - число под «денежным» ключом рядом с `currency` (в том же объекте или выше) →
//     сумма с валютой; сама `currency` тогда отдельной строкой не выводится;
//   - объект ровно из `amount` и `currency` → одна сумма;
//   - прочие числа — как есть (id, номера): разделитель разрядов их портит;
//   - ISO-дата и дата-время → по формату интерфейса; булево → да/нет; null → «—»;
//   - объект из `from`/`to` (и необязательной метки `currency`: сумма правки с её валютой) → изменение «было → станет» (kind "diff"); обёртка `changes` из таких
//     объектов раскрывается в строки родителя; форма `{field, from, to}` — одна строка изменения поля `field`;
//   - `*_id` с числом — ссылка на объект (`ref`, «№612»), такие строки идут первыми;
//   - `*_ids` скрывается, если соседний список ссылок (`*` / `*s`) содержит ровно те же ID;
//   - подпись поля — options.label(key) (словарь интерфейса), без него — ключ человеческим видом;
//   - подпись значения — options.valueLabel(key, value) для строки и null (вид таблицы цен, «основные» вместо пустого
//     сезона); без перевода значение выводится как есть.
// Денежные поля при закрытой ключу области `money` сервер срезает сам (STEP-06a),
// здесь их отсутствие ничем не заменяется.
//
// Функция чистая: форматтеры приходят снаружи (компонент даёт локаль и moment),
// спеки дают свои.

const MONEY_WORD = /(^|_)(amount|sum|total|price|cost|balance|debt|deposit|fee|pay)(_|$)/;
const NOT_MONEY_SUFFIX = /(_id|_ids|_count|_at|_percent|_type|_status)$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const NUMERIC = /^-?\d+(\.\d+)?$/;
const PNG_DATA_URL = /^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/;

const ID_KEY = /_id$/;

function isDiffObject(value) {
  if (!isPlainObject(value)) return false;
  const keys = Object.keys(value);
  // FT-228: сервер ставит метку валюты рядом с суммой правки — `currency` (ISO-код или null) допустима, но одна не правка
  if (!keys.some((k) => k === "from" || k === "to")) return false;
  if ("currency" in value && value.currency !== null && typeof value.currency !== "string") return false;
  return keys.every((k) => k === "from" || k === "to" || k === "currency");
}

export function isMoneyKey(key) {
  if (typeof key !== "string") return false;
  return MONEY_WORD.test(key) && !NOT_MONEY_SUFFIX.test(key);
}

// Подпись значения итога из словаря `agent_operations.values.<key>.<value>` — обращением к объекту сообщений, а не
// путём vue-i18n: значения бывают сохранёнными русскими строками со скобками, слешем и точками («Р/с», «Доходы -
// (Комиссия партнера - Расходы)», статусы бронирования), и разбор пути `a.b.c` такие ломает. Сначала язык интерфейса,
// затем запасной; null — пустое значение («none»). Без подписи — null: значение выводится как есть.
export function effectValueLabel(i18n, key, value) {
  if (!i18n) return null;
  const valueKey = value === null ? "none" : String(value);
  const locales = [i18n.locale, i18n.fallbackLocale].filter(Boolean);
  for (const locale of locales) {
    const messages = typeof i18n.getLocaleMessage === "function" ? i18n.getLocaleMessage(locale) : (i18n.messages || {})[locale];
    const dict = messages && messages.agent_operations && messages.agent_operations.values && messages.agent_operations.values[key];
    const label = dict && Object.prototype.hasOwnProperty.call(dict, valueKey) ? dict[valueKey] : null;
    if (typeof label === "string" && label !== "") return label;
  }
  return null;
}

export function humanizeKey(key) {
  const text = String(key).replace(/_/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isNumberLike(value) {
  return typeof value === "number" || (typeof value === "string" && NUMERIC.test(value));
}

function hasMatchingReferences(object, key) {
  if (!key.endsWith("_ids")) return false;
  const ids = object[key];
  if (!Array.isArray(ids) || !ids.length || !ids.every(isNumberLike)) return false;
  const base = key.slice(0, -4);
  return [base, `${base}s`].some((referenceKey) => {
    const refs = object[referenceKey];
    if (!Array.isArray(refs) || refs.length !== ids.length) return false;
    const remaining = new Set(ids.map(String));
    // Убираем только полное совпадение: частичные списки, повторы и записи без ID сохраняем.
    return remaining.size === ids.length && refs.every((ref) =>
      isPlainObject(ref) && isNumberLike(ref.id) && remaining.delete(String(ref.id)));
  });
}

const DEFAULT_FORMAT = {
  number: (n) => String(n),
  date: (s) => s,
  dateTime: (s) => s,
  yes: "yes",
  no: "no",
  empty: "—",
};

function valueNode(text) {
  return { kind: "value", text };
}

function moneyNode(amount, currency, fmt) {
  return { kind: "money", text: `${fmt.number(Number(amount))} ${currency}` };
}

// Узел дерева: { kind: "value" | "money", text, ref? } | { kind: "image", src } | { kind: "list", items: [узел] } |
// { kind: "diff", from: узел, to: узел } | { kind: "object", fields: [{ key, label, ...узел }] }.
export function effectNode(value, options = {}) {
  const fmt = { ...DEFAULT_FORMAT, ...(options.format || {}) };
  const key = options.key || null;
  const currency = options.currency || null;
  const label = options.label || null;
  const valueLabel = options.valueLabel || null;
  // parent — ключ объекта, в котором лежит поле: перевод значения может зависеть от места (код блокера ≠ код машины)
  const parent = options.parent || null;
  const child = (v, k, cur, par = null) => effectNode(v, { format: fmt, key: k, currency: cur, label, valueLabel, parent: par });
  const translated = key && valueLabel && (value === null || typeof value === "string") ? valueLabel(key, value, parent) : null;
  if (translated) return valueNode(translated);

  if (isDiffObject(value)) {
    // метка валюты самой правки сильнее валюты области; пустая/null — валюта области
    const diffCurrency = typeof value.currency === "string" && value.currency ? value.currency : currency;
    return { kind: "diff", from: child(value.from, key, diffCurrency), to: child(value.to, key, diffCurrency) };
  }

  if (value === null || value === undefined || value === "") return valueNode(fmt.empty);
  if (typeof value === "boolean") return valueNode(value ? fmt.yes : fmt.no);

  if (isNumberLike(value)) {
    if (currency && isMoneyKey(key)) return moneyNode(value, currency, fmt);
    // Не сумма — как есть: это может быть id или номер, разрядные пробелы его портят.
    return valueNode(String(value));
  }

  if (typeof value === "string") {
    // Подпись клиента от агента (STEP-11): владелец одобряет картинку, а не её хэш.
    // Только PNG в data URL — внешний адрес картинкой не рисуется.
    if (PNG_DATA_URL.test(value)) return { kind: "image", src: value };
    if (DATE_TIME.test(value)) return valueNode(fmt.dateTime(value));
    if (DATE.test(value)) return valueNode(fmt.date(value));
    return valueNode(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return valueNode(fmt.empty);
    const items = value.map((item) => child(item, key, currency));
    // цены по периодам, коды машин — одной строкой: столбик из «1. 2000 2. 1900» читается хуже
    const simple = value.every((item) => item === null || typeof item !== "object")
      && items.every((item) => item.kind === "value" || item.kind === "money");
    if (simple) return valueNode(items.map((item) => item.text).join(", "));
    return { kind: "list", items };
  }

  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    const ownCurrency = typeof value.currency === "string" && value.currency ? value.currency : null;
    if (ownCurrency && keys.length === 2 && keys.includes("amount") && isNumberLike(value.amount)) {
      return moneyNode(value.amount, ownCurrency, fmt);
    }
    const scopeCurrency = ownCurrency || currency;
    const labelOf = (k) => (label && label(k)) || humanizeKey(k);
    // {field, from, to}: изменение одного поля, имя поля — значением `field`.
    const fieldDiff = typeof value.field === "string" && value.field && ("from" in value || "to" in value);
    const moneyKeys = keys.filter((k) => k !== "currency" && isMoneyKey(k) && isNumberLike(value[k]));
    const changeKeys = isPlainObject(value.changes) ? Object.keys(value.changes) : [];
    const hasMoney = moneyKeys.length > 0 || (fieldDiff && isMoneyKey(value.field)) || changeKeys.some(isMoneyKey);
    const fields = [];
    keys.forEach((k) => {
      if (k === "currency" && ownCurrency && hasMoney) return;
      if (hasMatchingReferences(value, k)) return;
      if (fieldDiff && (k === "field" || k === "from" || k === "to")) {
        if (k !== "field") return;
        fields.push({ key: value.field, label: labelOf(value.field),
          ...child({ from: value.from, to: value.to }, value.field, scopeCurrency) });
        return;
      }
      const inner = value[k];
      // changes: {поле: {from, to}} — строки изменений прямо в родителе, без уровня «Changes».
      if (k === "changes" && isPlainObject(inner) && Object.keys(inner).length && Object.values(inner).every(isDiffObject)) {
        Object.keys(inner).forEach((f) => fields.push({ key: f, label: labelOf(f), ...child(inner[f], f, scopeCurrency) }));
        return;
      }
      const node = child(inner, k, scopeCurrency, key);
      if (ID_KEY.test(k) && node.kind === "value" && isNumberLike(inner)) node.ref = true;
      fields.push({ key: k, label: labelOf(k), ...node });
    });
    if (fields.length === 0) return valueNode(fmt.empty);
    // ссылки на объекты (`*_id`) — первыми: сначала «что», потом «как меняется»
    const ordered = [...fields.filter((f) => f.ref), ...fields.filter((f) => !f.ref)];
    return { kind: "object", fields: ordered };
  }

  return valueNode(String(value));
}
